"use client";

import React, { useState } from "react";
import { cn } from "@/lib/utils";
import { scrollIntoViewWithOffset } from "@/lib/smooth-scroll";
import { ChevronDown } from "lucide-react";

const HEADER_OFFSET_PX = 400;

interface AccordionItemProps {
	value: string;
	className?: string;
	children: React.ReactNode;
}

interface AccordionTriggerProps {
	className?: string;
	children: React.ReactNode;
	dataTestId?: string;
}

interface AccordionContentProps {
	className?: string;
	children: React.ReactNode;
}

interface AccordionProps {
	type?: "single" | "multiple";
	collapsible?: boolean;
	defaultValue?: string | string[];
	className?: string;
	children: React.ReactNode;
}

const AccordionContext = React.createContext<{
	openItems: string[];
	setOpenItem: (value: string | null) => void;
	isItemOpen: (value: string) => boolean;
	collapsible: boolean;
}>({
	openItems: [],
	setOpenItem: () => {},
	isItemOpen: () => false,
	collapsible: true,
});

export function Accordion({
	type: accordionType = "single",
	collapsible = true,
	defaultValue,
	className,
	children,
}: AccordionProps) {
	const getInitialOpenItems = (): string[] => {
		if (accordionType === "multiple" && Array.isArray(defaultValue)) {
			return defaultValue;
		}
		if (typeof defaultValue === "string" && defaultValue) {
			return [defaultValue];
		}
		return [];
	};

	const [openItems, setOpenItems] = useState<string[]>(getInitialOpenItems);

	const isItemOpen = React.useCallback(
		(value: string) => openItems.includes(value),
		[openItems]
	);

	const handleItemClick = React.useCallback(
		(value: string | null) => {
			if (typeof value !== "string") return;
			if (accordionType === "single") {
				if (openItems[0] === value && collapsible) {
					setOpenItems([]);
				} else {
					setOpenItems([value]);
				}
			} else {
				// multiple
				if (openItems.includes(value)) {
					setOpenItems((prev) =>
						collapsible ? prev.filter((x) => x !== value) : prev
					);
				} else {
					setOpenItems((prev) => [...prev, value]);
				}
			}
		},
		[accordionType, openItems, collapsible]
	);

	return (
		<AccordionContext.Provider
			value={{
				openItems,
				setOpenItem: handleItemClick,
				isItemOpen,
				collapsible,
			}}
		>
			<div className={cn("w-full", className)}>{children}</div>
		</AccordionContext.Provider>
	);
}

export function AccordionItem({
	value,
	className,
	children,
}: AccordionItemProps) {
	const { isItemOpen } = React.useContext(AccordionContext);
	const isOpen = isItemOpen(value);

	return (
		<div
			className={cn("border-none", className)}
			data-value={value}
			data-state={isOpen ? "open" : "closed"}
		>
			{children}
		</div>
	);
}

export function AccordionTrigger({
	className,
	children,
	dataTestId,
}: AccordionTriggerProps) {
	const { setOpenItem, isItemOpen } = React.useContext(AccordionContext);
	const buttonRef = React.useRef<HTMLButtonElement>(null);
	const [value, setValue] = React.useState<string>("");

	React.useEffect(() => {
		if (buttonRef.current) {
			const itemElement = buttonRef.current.closest("[data-value]");
			const foundValue = itemElement?.getAttribute("data-value") || "";
			setValue(foundValue);
		}
	}, []);

	const isOpen = isItemOpen(value);

	const handleClick = () => {
		if (buttonRef.current) {
			const itemElement = buttonRef.current.closest("[data-value]");
			const currentValue = itemElement?.getAttribute("data-value") || "";
			if (currentValue) {
				const wasOpen = isItemOpen(currentValue);
				const isOpening = !wasOpen;
				setOpenItem(currentValue);
				if (isOpening) {
					setTimeout(() => {
						scrollIntoViewWithOffset(
							itemElement ?? null,
							HEADER_OFFSET_PX
						);
					}, 150);
				}
			}
		}
	};

	return (
		<button
			ref={buttonRef}
			type="button"
			onClick={handleClick}
			aria-expanded={isOpen}
			data-testid={dataTestId}
			className={cn(
				"flex w-full items-center justify-between gap-4 rounded-t-[0.75rem] px-[1.5rem] py-[1rem] text-left transition-all hover:no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
				isOpen ? "rounded-b-none" : "rounded-[0.75rem]",
				className
			)}
		>
			{children}
			<ChevronDown
				className={cn(
					"h-5 w-5 shrink-0 text-gray-500 transition-transform duration-200",
					isOpen && "rotate-180"
				)}
				aria-hidden
			/>
		</button>
	);
}

export function AccordionContent({
	className,
	children,
}: AccordionContentProps) {
	const { isItemOpen } = React.useContext(AccordionContext);
	const contentRef = React.useRef<HTMLDivElement>(null);
	const [value, setValue] = React.useState<string>("");

	// Get value using ref callback for immediate access
	const setContentRef = React.useCallback((node: HTMLDivElement | null) => {
		contentRef.current = node;
		if (node) {
			const itemElement = node.closest("[data-value]");
			const foundValue = itemElement?.getAttribute("data-value") || "";
			if (foundValue) {
				setValue(foundValue);
			}
		}
	}, []);

	// Use layout effect to get value synchronously - run on every render until value is found
	React.useLayoutEffect(() => {
		if (contentRef.current && !value) {
			const itemElement = contentRef.current.closest("[data-value]");
			const foundValue = itemElement?.getAttribute("data-value") || "";
			if (foundValue) {
				setValue(foundValue);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Check if this content should be open
	// If value is empty, we haven't found it yet, so render it hidden so ref callback can run
	const isOpen = value !== "" && isItemOpen(value);

	// When answer opens, scroll it into view below the header for visibility and accessibility
	React.useEffect(() => {
		if (!isOpen || !contentRef.current) return;
		const timer = setTimeout(() => {
			scrollIntoViewWithOffset(contentRef.current, HEADER_OFFSET_PX);
		}, 150);
		return () => clearTimeout(timer);
	}, [isOpen]);

	// Smoothly animate open/close by transitioning max-height and opacity
	// Wrap children in a div that conditionally applies className (including padding) only when open
	// Use a large maxHeight for animation, but allow height to be auto for max-content behavior
	return (
		<div
			ref={setContentRef}
			className="relative overflow-hidden rounded-b-[0.75rem] transition-all duration-200 ease-out"
			style={{
				maxHeight: isOpen ? "9999px" : 0,
				opacity: isOpen ? 1 : 0,
				height: isOpen ? "auto" : 0,
			}}
		>
			<div className={isOpen ? className : undefined}>{children}</div>
		</div>
	);
}
